// Friendly, plain-language micro-copy for Otto's proposal card.
//
// Pure + deterministic (NO LLM): turns the structured intent/resolution into short, warm text that
// "speaks with the user" instead of dumping raw field ids (e.g. a bare "clear system" chip or a terse
// "For \"x\":" header). Kept as its own module per the project rule — new conversational surfaces get
// their own flow, never edits to the governed interpreter prompt. If we ever want LLM-varied phrasing,
// this is the seam: a small-model narrator can replace these helpers with these as the fallback.

// Human-readable label for a dimension/wildcard id, taken from the live schema maps so Otto shows the
// SAME name as the dashboard's filter window — never a raw backend key/id. Falls back to a cleaned,
// title-cased id (stripping any trailing UUID, e.g. "priority_elements.<uuid>" -> "Priority Elements";
// "sub_system" -> "Sub System") so an unmapped key still reads cleanly instead of leaking an id.
export const fieldLabel = (id, maps) => {
    if (id === "search") return "Search";
    const meta = maps && ((maps.dimMap && maps.dimMap[id]) || (maps.wildcardMap && maps.wildcardMap[id]));
    if (meta && meta.label) return meta.label;
    return String(id || "")
        .replace(/\.[0-9a-f]{8}-[0-9a-f-]+$/i, "") // drop a trailing UUID suffix (dynamic field keys)
        .replace(/[._]+/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim();
};

// "a", "a and b", "a, b and c".
export const humanJoin = items => {
    const list = (items || []).filter(Boolean);
    if (list.length <= 1) return list[0] || "";
    if (list.length === 2) return `${list[0]} and ${list[1]}`;
    return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
};

// Lead-in above a phrase's cross-field suggestions. `confirm` = we have a best guess (tap to change);
// `clarify` = genuinely unsure (we still surface our best candidates). Warm + plain-language — it frames
// the options as "what I think fits" rather than a terse "Which did you mean?". Pairs with the assistant's
// own message shown above it. `count` is how many pills actually render — the display floor often leaves
// exactly ONE, and the plural wording ("tap another…") must not promise options that aren't there.
export const suggestionLead = (decision, phrase, count = 2) => {
    if (count === 1) {
        return decision === "clarify"
            ? `Here’s the filter I think fits “${phrase}” — tap it if that’s what you mean:`
            : `I think “${phrase}” is this — tap it if that’s right:`;
    }
    return decision === "clarify"
        ? `Here are the filters I think could fit “${phrase}” — tap the one you mean:`
        : `I think “${phrase}” is this — tap another if I’ve got it wrong:`;
};

// Read-only history bubble for a stored CLARIFY turn. A resumed conversation can't reproduce the live
// card's grounded headline — the resolution isn't persisted — and the stored `assistant_message` is the
// PRE-resolve model guess the live card deliberately suppresses (it names fields that were never offered,
// e.g. description/notes). So history applies the SAME suppression rule and describes the exchange
// deterministically from the intent's own phrases instead.
export const historyClarifyLine = intent => {
    const phrases = ((intent && intent.ambiguous_terms) || []).map(t => t && t.phrase).filter(Boolean);
    if (!phrases.length) return "I asked for a little more detail to filter this the right way.";
    const quoted = phrases.map(p => `“${p}”`);
    return quoted.length === 1
        ? `${quoted[0]} could match more than one filter — I offered options to pick from.`
        : `Some of your words (${humanJoin(quoted)}) could match more than one filter — I offered options to pick from.`;
};

// Distinct human field labels across a result's suggestions, in order of appearance — the REAL fields Otto
// is offering for a phrase (deduped; a value-clarify with several options in one field collapses to one).
const distinctFieldLabels = (suggestions, maps) => {
    const labels = [];
    (suggestions || []).forEach(s => {
        const label = fieldLabel(s.field, maps);
        if (label && !labels.includes(label)) labels.push(label);
    });
    return labels;
};

// How many field names the multi-field headline lists before "and N more" — enough to orient, few enough
// that a broad probe (7+ matching fields is common since the data-driven baseline) never reads as a
// filter inventory.
const MAX_HEADLINE_FIELDS = 3;

const cappedFieldList = labels => {
    const shown = labels.slice(0, MAX_HEADLINE_FIELDS);
    const more = labels.length - shown.length;
    return more > 0 ? `${shown.join(", ")} and ${more} more` : humanJoin(shown);
};

// The strictly top-scored, tappable suggestion across a result — the pick the headline LEADS with.
// Text-search entries never lead ("search descriptions" is a fallback, not a best guess), and a tie
// (another suggestion at the same top score — e.g. two same-name options in twin fields) returns null:
// leading with an arbitrary winner would steer the user, so ties keep the even-handed enumeration.
const topPick = suggestions => {
    const scorable = (suggestions || []).filter(
        s => s && s.name && s.method !== "text" && s.value_kind !== "text" && s.field !== "search"
    );
    if (!scorable.length) return null;
    const best = scorable.reduce((a, b) => ((b.score || 0) > (a.score || 0) ? b : a));
    const tied = scorable.some(s => s !== best && (s.score || 0) === (best.score || 0));
    return tied ? null : best;
};

// Grounded clarification headline, built from the RESOLVED options (Stage B) — NOT the interpreter's
// pre-resolve `assistant_message`. The model writes that message BEFORE the resolver runs, so it routinely
// names fields Otto never actually offers (free-text description/notes/search get dropped as clarify
// options) AND misses the fields only the data-driven probe discovers — the resolver's top pick is often a
// field the model never named. Speaking from the resolved options keeps Otto's words matched to its
// buttons. When one suggestion strictly outscores the rest, the headline LEADS with it ("Sounds like you
// might mean …") instead of enumerating fields; ties/no-scores fall back to a SHORT field list (capped at
// MAX_HEADLINE_FIELDS + "and N more"). Returns "" when there's no resolved option to describe (the caller
// then shows CLARIFY_FALLBACK instead of the model's misleading guess).
export const clarificationHeadline = (results, maps) => {
    // A BROWSE result (FTO-10) frames the whole card: the user named a FIELD, so the headline says so
    // instead of guessing between value matches. Combined mode (the word is also literally a value in
    // another field — "system" is a Recommendation Type option) names both readings.
    const browsed = (results || []).find(r => r && r.browse);
    if (browsed) {
        const other = (results || []).some(r => r && !r.browse && (r.suggestions || []).length);
        return other
            ? `“${browsed.phrase}” is your ${browsed.browse.label} filter — tap the option(s) you want below. (If you meant it as a value, those matches are underneath.)`
            : `${browsed.browse.label} is one of your filters — tap the option(s) you want and Apply.`;
    }
    // A THRESHOLD result (FTO-12): the named field is numeric/date, so the ask is an operator+value,
    // carried by the prompt wildcard pill's editor — or a typed reply ("over 50k") resolved via the
    // question store. Several fields can share the mention ("cost" names Construction Cost, Project
    // Cost, …): one pill each, and picking IS the field clarification.
    const threshold = (results || []).find(r => r && r.threshold);
    if (threshold) {
        const fields = threshold.threshold.fields || [];
        const one = fields.length === 1 ? fields[0] : null;
        if (one) {
            const example = one.value_type === "date" ? "“before 2027”" : "“over 50k” or “between 10k and 100k”";
            return `${one.label} is a ${one.value_type === "date" ? "date" : "number"} filter — tap the pill below to set a threshold, or tell me one (e.g. ${example}).`;
        }
        return `“${threshold.threshold.phrase}” matches ${fields.length} of your number filters — tap the one you mean below and set a threshold.`;
    }
    const withOptions = (results || []).filter(r => r && (r.suggestions || []).length);
    // The genuine question is the clarify / group (multi) results; a lone `confirm` is a best guess we can
    // still describe if that's all there is. Prefer the real asks so a mixed message (one committed + one
    // ambiguous) is framed by the ambiguous term, not a confident guess.
    const asks =
        withOptions.filter(r => r.decision === "clarify" || r.multi).length > 0
            ? withOptions.filter(r => r.decision === "clarify" || r.multi)
            : withOptions.filter(r => r.decision === "confirm");
    if (!asks.length) return "";
    if (asks.length > 1) return "Some of your words could match more than one filter — tap the ones you mean below.";
    const { phrase, suggestions } = asks[0];
    const labels = distinctFieldLabels(suggestions, maps);
    if (!labels.length) return "";
    if (labels.length === 1) return `“${phrase}” maps to your ${labels[0]} filter — tap the option you mean below.`;
    const top = topPick(suggestions);
    if (top) {
        return (
            `Sounds like you might mean “${top.name}” (${fieldLabel(top.field, maps)}) — ` +
            "tap it below, or pick one of the other options."
        );
    }
    return `“${phrase}” could match your ${cappedFieldList(labels)} filters — tap the one you mean below.`;
};

// Top-of-card copy for a clarification when nothing resolved to an option we can describe (so we never fall
// back to the model's pre-resolve guess, which may name fields — description/notes/search — we don't show).
export const CLARIFY_FALLBACK = "I want to filter this the right way — could you add a little more detail?";

// Gentle fallback shown when Otto made a guess / asked (confirm or clarify): invites the user to refine
// instead of leaving them stuck. Deterministic copy — no model call.
export const refineHint = () => "If I’ve misread you, add a bit more detail and I’ll try again.";

// How many viable alternatives we NAME in a conflict/blocked message before trailing off — enough to
// be genuinely helpful, few enough to stay a sentence.
const MAX_NAMED_ALTERNATIVES = 5;

const namedAlternatives = alternatives => {
    const names = (alternatives || []).map(a => `“${a.name}”`);
    const shown = names.slice(0, MAX_NAMED_ALTERNATIVES);
    const more = names.length - shown.length;
    return more > 0 ? `${shown.join(", ")} and ${more} more` : humanJoin(shown);
};

// A selected combination that can't show data: the numbered selection that breaks it, framed against
// the earlier picks, plus what IS possible for that field. `conflict` comes from evaluateFeasibility:
// { field, breaker: {match, order}, alternatives }.
export const conflictLine = (conflict, maps) => {
    const label = fieldLabel(conflict.field, maps);
    const { match, order } = conflict.breaker;
    const head = `⚠ (${order}) ${label}: “${match.name}” has no results together with your earlier selections, so this combination can’t show data.`;
    const alts = namedAlternatives(conflict.alternatives);
    return alts
        ? `${head} With those, the ${label} options that do have results are ${alts}.`
        : `${head} No ${label} option has results with them — try removing one of your earlier picks.`;
};

// The user tapped a pill that would empty the dashboard under their current numbered selections:
// explain instead of selecting it, and name what IS possible for that field.
export const blockedPickLine = (match, alternatives, maps) => {
    const label = fieldLabel(match.field, maps);
    const head = `“${match.name}” has no results with your current selections, so I didn’t add it.`;
    const alts = namedAlternatives(alternatives);
    return alts
        ? `${head} ${label} options that do fit: ${alts}.`
        : `${head} No ${label} option fits — remove one of your numbered picks to open this up.`;
};

// The live record count Otto shows by the Apply button once a filter is selected — how many records the
// dashboard would show if the current picks were applied (from the feasibility cascade's `total`). Thousands
// are grouped and the noun is pluralized ("1 Record", "550 Records"); a null/absent total renders nothing
// (backend didn't report it), so callers can render this unconditionally.
export const recordsLabel = total => {
    if (total == null || typeof total !== "number" || !Number.isFinite(total)) return "";
    return `${total.toLocaleString()} ${total === 1 ? "Record" : "Records"}`;
};

// Friendly sentence for `intent.clears` (dimensions/wildcards the user asked to drop). Mirrors the
// user's own phrasing: the removal happens on Apply, as requested.
export const clearsLine = (clears, maps) => {
    const labels = (clears || []).map(id => fieldLabel(id, maps));
    if (!labels.length) return "";
    const noun = labels.length > 1 ? "filters" : "filter";
    return `↩ When you Apply, I’ll clear your ${humanJoin(labels)} ${noun}, as requested.`;
};

// Friendly sentence for `intent.clear_all`.
export const clearAllLine = () => "↩ When you Apply, I’ll clear all of your current filters.";

// Human summary of the dashboard's currently-applied filters, built by orchestrator.buildActiveSummary
// as [{ label, names: [...] }] (names resolved from the catalog where known). Shown when the user asked to
// remove something that isn't applied, so they can see what actually IS. e.g.
// "Category (Code Compliance), Trade (HVAC) and Years (2025–2028)".
export const activeFilterSummaryText = (summary = []) =>
    humanJoin(
        (summary || [])
            .filter(s => s && s.label)
            .map(s => (s.names && s.names.length ? `${s.label} (${s.names.join(", ")})` : s.label))
    );

// The removal card's headline. Removals auto-apply (user decision), so this SPEAKS IN THE PAST TENSE about
// what was done — value-level removes ("Category: Code Compliance") and whole-field clears ("your Trade
// filter") — and, when a requested target wasn't actually applied, says so and (if nothing at all matched)
// lists the filters that ARE applied. Deterministic copy, no LLM (same seam as the rest of this module).
export const removalMessage = ({ removals = [], clears = [], clearWildcards = [], clearYear = false, notApplied = [], activeSummary = [] } = {}, maps) => {
    const removedNames = (removals || []).map(r => `${fieldLabel(r.field, maps)}: ${r.name}`);
    const clearedLabels = [
        ...(clears || []).map(c => c.label || fieldLabel(c.id, maps)),
        ...(clearWildcards || []).map(c => (c && c.label) || fieldLabel(c && c.id != null ? c.id : c, maps)),
        ...(clearYear ? ["Years"] : [])
    ];
    const naPhrases = (notApplied || []).map(n => `“${n.phrase}”`);

    const parts = [];
    if (removedNames.length) parts.push(`removed ${humanJoin(removedNames)}`);
    if (clearedLabels.length) {
        const noun = clearedLabels.length > 1 ? "filters" : "filter";
        parts.push(`cleared your ${humanJoin(clearedLabels)} ${noun}`);
    }

    // Something WAS removed: past-tense confirmation, plus a gentle note about any target that wasn't applied.
    if (parts.length) {
        let msg = `Done — I ${humanJoin(parts)}.`;
        if (naPhrases.length) {
            msg += ` I left ${humanJoin(naPhrases)} alone — ${naPhrases.length > 1 ? "those aren’t" : "that isn’t"} currently applied.`;
        }
        return msg;
    }

    // Nothing matched an applied filter — tell them, and show what IS applied so they can adjust.
    const na = humanJoin(naPhrases);
    const subject = naPhrases.length > 1 ? `${na} aren’t` : `${na || "That filter"} isn’t`;
    const active = activeFilterSummaryText(activeSummary);
    return active
        ? `${subject} currently applied. Your active filters are ${active}.`
        : `${subject} currently applied — you don’t have any filters applied right now.`;
};
